// GuestFeedbackPage — Premium guest feedback form (V3 hospitality design)
//
// Supported public mode:
//   /feedback/t/:token  → submitFeedbackByToken
// Legacy location-only links render an unavailable state and cannot submit.

import { useEffect, useRef, useState } from "react";
import {
  Heart, AlertTriangle, Lightbulb, HelpCircle, ShieldAlert, ConciergeBell,
  CarFront, PlugZap, Droplets, SquareParking, Shield, Sparkles, Wrench,
  MessageCircle, Bell, Plus, Check, Send, Copy, Inbox, PhoneCall,
  CheckCircle2, Lock, UserRoundCheck, Clock, ShieldCheck,
  MessageSquare, Phone, Mail,
} from "lucide-react";
import { api } from "../api";

// ── Design tokens ──────────────────────────────────────────────────────────────

const FONTS = {
  sans:  '"Instrument Sans", ui-sans-serif, sans-serif',
  serif: '"Instrument Serif", ui-serif, serif',
  mono:  '"JetBrains Mono", ui-monospace, monospace',
};

const C = {
  paper:      "#FBFAF7",
  card:       "#FFFFFF",
  cream:      "#F4F0E8",
  stone50:    "#F6F5F1",
  stone100:   "#ECEAE3",
  stone200:   "#DEDBD1",
  stone300:   "#C4BFB1",
  stone400:   "#9A9485",
  stone500:   "#6B6557",
  stone600:   "#4A4537",
  ink:        "#1A1712",
  brass50:    "#F3ECDC",
  brass300:   "#D8BE86",
  brass500:   "#B08938",
  brass600:   "#8F6E2B",
  brass700:   "#6E521C",
  cobalt50:   "#ECF0F7",
  cobalt500:  "#1F3A8A",
  cobalt600:  "#1A327A",
  forest50:   "#E4EDE3",
  forest500:  "#2C6E45",
  amber50:    "#F7ECCF",
  rust50:     "#F4DDD5",
  rust500:    "#B0432F",
  rust700:    "#732517",
} as const;

// ── Types ──────────────────────────────────────────────────────────────────────

// Widened to `string` (not a closed union): the service list is now driven by
// per-location configuration and may include admin-added custom option ids
// the static list below has never heard of.
type ServiceType  = string;
type FeedbackType = "compliment" | "complaint" | "suggestion" | "inquiry" | "safety_concern" | "service_request";
type PrefContact  = "sms" | "phone" | "email";

interface DynamicField { label: string; placeholder: string; optional?: boolean; mapTo?: "ticketOrPlate" }

interface ServiceDef {
  id:          ServiceType;
  icon:        React.ReactNode;
  label:       string;
  placeholder: string;
  fields:      DynamicField[];
}

interface FeedbackTypeDef {
  id:    FeedbackType;
  cls:   string;
  icon:  React.ReactNode;
  title: string;
  sub:   string;
  tint:  string;
  tc:    string;
}

// ── Data ───────────────────────────────────────────────────────────────────────

const TYPES: FeedbackTypeDef[] = [
  { id: "compliment",      cls: "compliment", icon: <Heart size={19} strokeWidth={2} />,        title: "Compliment",      sub: "A great experience",     tint: C.cobalt50,  tc: C.cobalt500 },
  { id: "complaint",       cls: "complaint",  icon: <AlertTriangle size={19} strokeWidth={2} />, title: "Complaint",       sub: "Something went wrong",   tint: C.rust50,    tc: C.rust500   },
  { id: "suggestion",      cls: "suggestion", icon: <Lightbulb size={19} strokeWidth={2} />,     title: "Suggestion",      sub: "An idea to improve",     tint: C.amber50,   tc: C.brass600  },
  { id: "inquiry",         cls: "inquiry",    icon: <HelpCircle size={19} strokeWidth={2} />,    title: "Inquiry",         sub: "Need information",       tint: C.stone50,   tc: C.stone500  },
  { id: "safety_concern",  cls: "safety",     icon: <ShieldAlert size={19} strokeWidth={2} />,   title: "Safety concern",  sub: "An immediate concern",   tint: C.rust50,    tc: C.rust700   },
  { id: "service_request", cls: "service",    icon: <ConciergeBell size={19} strokeWidth={2} />, title: "Service request", sub: "Need assistance",        tint: C.forest50,  tc: C.forest500 },
];

const SERVICES: ServiceDef[] = [
  {
    id: "valet", icon: <CarFront size={16} strokeWidth={2} />, label: "Valet",
    placeholder: "Tell us what happened with your valet experience…",
    fields: [
      { label: "Vehicle",           placeholder: "e.g. Black Tesla Model Y" },
      { label: "Ticket number",     placeholder: "e.g. 1042",     optional: true, mapTo: "ticketOrPlate" },
      { label: "Approximate time",  placeholder: "e.g. 6:30 pm",  optional: true },
    ],
  },
  {
    id: "ev", icon: <PlugZap size={16} strokeWidth={2} />, label: "EV charging",
    placeholder: "Tell us about your EV charging experience…",
    fields: [
      { label: "Station",  placeholder: "e.g. EV-3",           optional: true },
      { label: "Vehicle",  placeholder: "Make & model",         optional: true },
    ],
  },
  {
    id: "car_wash", icon: <Droplets size={16} strokeWidth={2} />, label: "Car wash",
    placeholder: "What would you like us to know about the car wash?",
    fields: [
      { label: "Vehicle",  placeholder: "Make & model",     optional: true },
      { label: "Service",  placeholder: "e.g. Full detail", optional: true },
    ],
  },
  {
    id: "parking", icon: <SquareParking size={16} strokeWidth={2} />, label: "Parking",
    placeholder: "Describe your parking experience…",
    fields: [
      { label: "Parking space",   placeholder: "e.g. B2-114",     optional: true },
      { label: "Gate / entrance", placeholder: "e.g. North gate",  optional: true },
    ],
  },
  {
    id: "front_desk", icon: <ConciergeBell size={16} strokeWidth={2} />, label: "Front desk",
    placeholder: "How can the front desk help?",
    fields: [
      { label: "Topic",            placeholder: "Package · visitor · access", optional: true },
      { label: "Approximate time", placeholder: "e.g. 3:15 pm",              optional: true },
    ],
  },
  {
    id: "security", icon: <Shield size={16} strokeWidth={2} />, label: "Security",
    placeholder: "Describe the incident…",
    fields: [
      { label: "Location", placeholder: "Where did it happen?" },
      { label: "Time",     placeholder: "e.g. 11:40 pm", optional: true },
    ],
  },
  {
    id: "cleaners", icon: <Sparkles size={16} strokeWidth={2} />, label: "Cleaners",
    placeholder: "What cleaning issue would you like us to know about?",
    fields: [
      { label: "Unit or common area", placeholder: "e.g. Unit 1708 · lobby", optional: true },
    ],
  },
  {
    id: "building_maintenance", icon: <Wrench size={16} strokeWidth={2} />, label: "Building maintenance",
    placeholder: "Describe the maintenance issue…",
    fields: [
      { label: "Unit or common area", placeholder: "e.g. Unit 1708 · gym" },
      { label: "Issue type",          placeholder: "e.g. Plumbing, HVAC", optional: true },
    ],
  },
  {
    id: "other", icon: <MessageCircle size={16} strokeWidth={2} />, label: "Other",
    placeholder: "Tell us more…",
    fields: [],
  },
];

// Server-configured option ids that don't literally match a static SERVICES
// entry but should still resolve to one for icon/placeholder/fields — kept as
// a small alias table rather than renaming the static list, since the static
// list's ids are also used as fallbacks for the legacy dev route below.
const SERVICE_ID_ALIASES: Record<string, string> = { ev_charging: "ev" };

const STATIC_SERVICE_BY_ID: Record<string, ServiceDef> = Object.fromEntries(
  SERVICES.map(s => [s.id, s])
);

function staticServiceLookup(id: string): ServiceDef | undefined {
  return STATIC_SERVICE_BY_ID[id] ?? STATIC_SERVICE_BY_ID[SERVICE_ID_ALIASES[id] ?? ""];
}

// Merges a location's server-configured {id,label} service options with the
// static list's rich UI metadata (icon/placeholder/dynamic fields). Unknown
// ids (admin-added custom options) fall back to a generic chip with no
// dynamic fields — label is always the server-configured one, since renames
// must be reflected even for known ids.
function buildEffectiveServices(options: Array<{ id: string; label: string }>): ServiceDef[] {
  return options.map(opt => {
    const staticDef = staticServiceLookup(opt.id);
    return {
      id:          opt.id,
      icon:        staticDef?.icon ?? <MessageCircle size={16} strokeWidth={2} />,
      label:       opt.label,
      placeholder: staticDef?.placeholder ?? "Tell us more…",
      fields:      staticDef?.fields ?? [],
    };
  });
}

const RATING_TEXT = ["", "Very poor", "Needs improvement", "Average", "Good", "Excellent"];

// ── Inline styles ──────────────────────────────────────────────────────────────

const ROOT: React.CSSProperties = {
  fontFamily: FONTS.sans,
  color: C.ink,
  WebkitFontSmoothing: "antialiased",
  background: `radial-gradient(900px 500px at 50% -5%, #FFFDF8 0%, transparent 60%), ${C.paper}`,
  minHeight: "100vh",
  maxWidth: "100vw",
  overflowX: "hidden",
  boxSizing: "border-box",
  // iPhone Safari home-indicator / Android gesture-nav clearance.
  paddingBottom: "calc(32px + env(safe-area-inset-bottom, 0px))",
  paddingTop: "env(safe-area-inset-top, 0px)",
};

const WRAP: React.CSSProperties = {
  maxWidth: 560, margin: "0 auto", padding: "0 clamp(14px, 4vw, 20px)",
};

const SEC: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.stone200}`, borderRadius: 14,
  padding: "clamp(16px, 4.6vw, 22px)", boxShadow: "0 1px 2px rgba(26,23,18,0.03)",
};

const INP: React.CSSProperties = {
  height: 46, border: `1px solid ${C.stone200}`, borderRadius: 10,
  padding: "0 14px", fontFamily: FONTS.sans, fontSize: 15, color: C.ink,
  background: C.card, width: "100%", outline: "none", boxSizing: "border-box",
  transition: "border-color 140ms, box-shadow 140ms",
};

const SUBMIT_BASE: React.CSSProperties = {
  width: "100%", height: 56, border: "none", borderRadius: 14,
  background: C.cobalt500, color: "#fff", fontFamily: FONTS.sans,
  fontSize: 16, fontWeight: 600, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
  marginTop: 14, boxShadow: "0 12px 26px -10px rgba(31,58,138,0.55)",
  transition: "background 140ms",
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function StepHeader({ num, title, optional }: { num: number; title: string; optional?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
      <span style={{
        width: 24, height: 24, borderRadius: 999, background: C.cream,
        border: `1px solid ${C.brass300}`, color: C.brass700,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700, flexShrink: 0,
      }}>{num}</span>
      <span style={{ fontSize: 16.5, fontWeight: 600, letterSpacing: "-0.01em", color: C.ink }}>{title}</span>
      {optional && (
        <span style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 600, color: C.stone400, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Optional
        </span>
      )}
    </div>
  );
}

function FieldError({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <p id={id} role="alert" style={{ margin: "6px 0 0", fontSize: 12.5, color: C.rust500, lineHeight: 1.4 }}>
      {children}
    </p>
  );
}

function StarRating({ rating, onChange }: { rating: number; onChange: (r: number) => void }) {
  const [hover, setHover] = useState(0);
  const lit = hover || rating;
  return (
    <div>
      <div role="radiogroup" aria-label="Rating" className="gf-rating-row" style={{ display: "flex", justifyContent: "center", gap: 8, padding: "6px 0 2px" }}>
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n} type="button"
            role="radio" aria-checked={rating === n}
            aria-label={`${n} out of 5 stars — ${RATING_TEXT[n]}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(rating === n ? 0 : n)}
            style={{
              background: "none", border: 0, padding: 4, cursor: "pointer", lineHeight: 0,
              minWidth: 44, minHeight: 44, display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: n <= lit ? C.brass500 : C.stone200,
              transform: hover === n ? "scale(1.15) translateY(-2px)" : "scale(1)",
              transition: "transform 140ms, color 120ms",
            }}
          >
            <svg className="gf-rating-star" width={46} height={46} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} fill={n <= lit ? C.brass500 : "transparent"} style={{ transition: "fill 120ms" }}>
              <path d="M12 2.5l2.9 6.1 6.6.9-4.8 4.6 1.2 6.6L12 18.6 6 21.3l1.2-6.6L2.4 9.5l6.6-.9z" strokeLinejoin="round" />
            </svg>
          </button>
        ))}
      </div>
      <div style={{
        textAlign: "center", minHeight: 24, marginTop: 12, fontSize: 16,
        fontFamily: FONTS.serif, fontStyle: "italic", color: C.brass700,
        opacity: lit ? 1 : 0, transition: "opacity 160ms",
      }}>
        {RATING_TEXT[lit] ?? ""}
      </div>
    </div>
  );
}

function TypeCard({ def, selected, onClick }: { def: FeedbackTypeDef; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button" onClick={onClick} aria-pressed={selected}
      style={{
        display: "flex", alignItems: "flex-start", gap: 12, padding: 14, minWidth: 0,
        minHeight: 44,
        border: selected ? `2px solid ${def.tc}` : `1px solid ${C.stone200}`,
        borderRadius: 11, background: selected ? `color-mix(in srgb, ${def.tint} 55%, #fff)` : C.card,
        cursor: "pointer", textAlign: "left", fontFamily: FONTS.sans, position: "relative",
        boxShadow: selected ? `0 0 0 1px ${def.tc}, 0 8px 20px -12px ${def.tc}` : undefined,
        transform: "translateY(0)",
        transition: "all 140ms",
      }}
    >
      <span style={{
        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: def.tint, color: def.tc,
      }}>
        {def.icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{def.title}</div>
        <div style={{ fontSize: 12, color: C.stone500, marginTop: 2, lineHeight: 1.35 }}>{def.sub}</div>
      </div>
      {selected && (
        <span style={{
          position: "absolute", top: 10, right: 10, width: 18, height: 18,
          borderRadius: 999, background: def.tc, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Check size={12} strokeWidth={3} />
        </span>
      )}
    </button>
  );
}

function ServiceChip({ svc, selected, onClick }: { svc: ServiceDef; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button" onClick={onClick} aria-pressed={selected}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7, height: 44,
        padding: "0 15px", borderRadius: 999,
        border: `1px solid ${selected ? C.ink : C.stone200}`,
        background: selected ? C.ink : C.card,
        cursor: "pointer", fontFamily: FONTS.sans, fontSize: 13.5, fontWeight: 500,
        color: selected ? "#fff" : C.stone600,
        transition: "all 140ms",
      }}
    >
      <span style={{ color: selected ? C.brass300 : C.stone400, display: "flex" }}>
        {svc.icon}
      </span>
      {svc.label}
    </button>
  );
}

// ── Page component ─────────────────────────────────────────────────────────────

type Props =
  | { token: string }
  | { invalidLink: true };

export default function GuestFeedbackPage(props: Props) {
  const tokenMode = "token" in props && props.token.length > 0;

  // Form state
  const [rating,     setRating]    = useState(0);
  const [fbType,     setFbType]    = useState<FeedbackType | null>(null);
  const [service,    setService]   = useState<ServiceType | null>(null);
  const [message,    setMessage]   = useState("");
  const [dynValues,  setDynValues] = useState<string[]>([]);
  const [ticketVal,  setTicketVal] = useState("");   // merged from dynamic fields with mapTo="ticketOrPlate"

  // Contact
  const [showContact,  setShowContact]  = useState(false);
  const [contactName,  setContactName]  = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [prefContact,  setPrefContact]  = useState<PrefContact>("sms");

  // Page states
  const [submitting,     setSubmitting]     = useState(false);
  const [done,           setDone]           = useState(false);
  const [referenceNum,   setReferenceNum]   = useState<string | null>(null);
  const [disabled,       setDisabled]       = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [tokenResolving, setTokenResolving] = useState<boolean>(true);
  const [copied,         setCopied]         = useState(false);
  // A configured logo URL that 404s/fails to load must fall back to the
  // default mark, not render a broken-image icon.
  const [logoFailed,     setLogoFailed]     = useState(false);
  const [tokenSettings,  setTokenSettings]  = useState<{
    requireRating: boolean;
    allowAnonymousGuest: boolean;
    brandName?: string;
    logoUrl?: string;
    formTitle?: string;
    formSubtitle?: string;
    showPoweredByAtrium: boolean;
    publicReviewerLabel: string;
    publicResponseCommitmentEnabled: boolean;
    publicResponseTargetHours?: number;
    publicResponseRequestEnabled: boolean;
    publicResponseContactMethods: PrefContact[];
    serviceQuestionEnabled: boolean;
    serviceQuestionRequired: boolean;
    serviceOptions: Array<{ id: string; label: string }>;
  }>({
    requireRating: false, allowAnonymousGuest: true, showPoweredByAtrium: true, publicReviewerLabel: "the service team",
    publicResponseCommitmentEnabled: false,
    publicResponseRequestEnabled: true,
    publicResponseContactMethods: ["sms", "phone", "email"],
    serviceQuestionEnabled: true, serviceQuestionRequired: false, serviceOptions: [],
  });

  // SERVICES below is used only as an icon/placeholder/fields lookup table
  // keyed by known id (buildEffectiveServices), never as a fallback option
  // list. An id the table doesn't recognize (a custom admin option) still
  // renders correctly with a generic icon and no dynamic fields.
  const services = buildEffectiveServices(tokenSettings.serviceOptions);
  // A saved config with zero ACTIVE options must hide the question entirely
  // (nothing valid to select), not show it with an empty/explanatory state —
  // this also protects against submitting a stale selection.
  const showServiceStep = tokenSettings.serviceQuestionEnabled && services.length > 0;
  // When the location doesn't allow anonymous guest feedback, contact info
  // is mandatory — the "Need a response?" section can't stay collapsed
  // (collapsed + required would silently block submission with no visible
  // field to fill in).
  // Valid token links always render the optional or required contact control;
  // invalid/non-token links never make a response promise.
  const allowedContactMethods = tokenSettings.publicResponseContactMethods;
  const responseRequestAvailable = tokenMode && !disabled && tokenSettings.publicResponseRequestEnabled && allowedContactMethods.length > 0;
  const requireContact = responseRequestAvailable && !tokenSettings.allowAnonymousGuest;
  const showResponseCommitment = responseRequestAvailable
    && tokenSettings.publicResponseCommitmentEnabled
    && Number.isInteger(tokenSettings.publicResponseTargetHours)
    && (tokenSettings.publicResponseTargetHours ?? 0) >= 1
    && (tokenSettings.publicResponseTargetHours ?? 0) <= 168;

  // ── Settings resolve ─────────────────────────────────────────────────────────
  // Only token-bound public links resolve settings.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!tokenMode) {
          if (!cancelled) setDisabled(true);
          return;
        }
        const res = await api.resolveFeedbackPublicToken({ token: props.token });
        if (cancelled) return;
        const serviceOptions = Array.isArray(res.serviceOptions) ? res.serviceOptions : [];
        setTokenSettings({
          requireRating:       res.requireRating === true,
          allowAnonymousGuest: res.allowAnonymousGuest === true,
          brandName:           res.brandName    ?? undefined,
          logoUrl:             res.logoUrl      ?? undefined,
          formTitle:           res.formTitle    ?? undefined,
          formSubtitle:        res.formSubtitle ?? undefined,
          showPoweredByAtrium: res.showPoweredByAtrium !== false,
          publicReviewerLabel: res.publicReviewerLabel?.trim() || "the service team",
          publicResponseCommitmentEnabled: res.publicResponseCommitmentEnabled === true,
          publicResponseTargetHours: res.publicResponseTargetHours,
          publicResponseRequestEnabled: res.publicResponseRequestEnabled !== false,
          publicResponseContactMethods: (res.publicResponseContactMethods ?? []).filter((m): m is PrefContact => m === "sms" || m === "phone" || m === "email"),
          serviceQuestionEnabled:  res.serviceQuestionEnabled !== false,
          serviceQuestionRequired: res.serviceQuestionRequired === true,
          serviceOptions,
        });
        const serviceTypeDefault = (res as { serviceTypeDefault?: string }).serviceTypeDefault;
        if (serviceTypeDefault) {
          const svc = serviceOptions.find(s => s.id === serviceTypeDefault);
          if (svc) setService(svc.id);
        }
      } catch {
        if (!cancelled) setDisabled(true);
      } finally {
        if (!cancelled) setTokenResolving(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset dynamic values when service changes
  useEffect(() => {
    const svc = services.find(s => s.id === service);
    setDynValues((svc?.fields ?? []).map(() => ""));
    setTicketVal("");
    if (svc) setMessage("");
  }, [service]);

  // Contact info is mandatory when anonymous guest feedback is disallowed —
  // force the section open so its (now-required) fields are always visible.
  useEffect(() => {
    if (requireContact) setShowContact(true);
  }, [requireContact]);

  useEffect(() => {
    if (!responseRequestAvailable) {
      setShowContact(false);
      setPrefContact("sms");
      return;
    }
    if (!allowedContactMethods.includes(prefContact)) setPrefContact(allowedContactMethods[0]);
  }, [responseRequestAvailable, allowedContactMethods.join(","), prefContact]);

  // ── Validation + focus management ───────────────────────────────────────────
  const ratingSectionRef  = useRef<HTMLDivElement>(null);
  const serviceSectionRef = useRef<HTMLDivElement>(null);
  const messageRef        = useRef<HTMLTextAreaElement>(null);
  const contactNameRef    = useRef<HTMLInputElement>(null);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);

  // Move focus to the confirmation heading so keyboard/screen-reader users
  // land on the success state instead of a silently-replaced page.
  useEffect(() => {
    if (done) successHeadingRef.current?.focus();
  }, [done]);

  const [fieldErrors, setFieldErrors] = useState<{
    rating?: string; service?: string; message?: string; contact?: string;
  }>({});

  function validate(): { ok: true } | { ok: false; message: string; focus: React.RefObject<HTMLElement | null> } {
    const errs: typeof fieldErrors = {};
    if (tokenSettings.requireRating && !rating) {
      errs.rating = "Please select a rating.";
    }
    // showServiceStep already implies the question is enabled AND has active
    // options — a required-but-unrenderable question can never block submit.
    if (showServiceStep && tokenSettings.serviceQuestionRequired && !service) {
      errs.service = "Please select which team or service this is about.";
    }
    if (!message.trim()) {
      errs.message = "Please tell us more about your experience.";
    }
    if (requireContact && !contactName.trim() && !contactEmail.trim()) {
      errs.contact = "Please share your name or email so we can follow up.";
    }
    if (showContact && responseRequestAvailable) {
      if ((prefContact === "sms" || prefContact === "phone") && !contactPhone.trim()) errs.contact = "Please provide a phone number for follow-up.";
      if (prefContact === "email" && !contactEmail.trim()) errs.contact = "Please provide an email address for follow-up.";
    }
    setFieldErrors(errs);
    if (errs.rating)  return { ok: false, message: errs.rating,  focus: ratingSectionRef };
    if (errs.service) return { ok: false, message: errs.service, focus: serviceSectionRef };
    if (errs.message) return { ok: false, message: errs.message, focus: messageRef };
    if (errs.contact) return { ok: false, message: errs.contact, focus: contactNameRef };
    return { ok: true };
  }

  // Clear a field's inline error as soon as it becomes valid again — do not
  // make the user re-submit just to find out the error cleared.
  useEffect(() => { if (fieldErrors.rating && rating) setFieldErrors(fe => ({ ...fe, rating: undefined })); }, [rating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (fieldErrors.service && service) setFieldErrors(fe => ({ ...fe, service: undefined })); }, [service]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (fieldErrors.message && message.trim()) setFieldErrors(fe => ({ ...fe, message: undefined })); }, [message]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (fieldErrors.contact && (contactName.trim() || contactEmail.trim())) setFieldErrors(fe => ({ ...fe, contact: undefined }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactName, contactEmail]);

  // ── Submit ───────────────────────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const result = validate();
    if (!result.ok) {
      // The inline FieldError next to the relevant control (role="alert")
      // both explains and announces the problem — no redundant top banner
      // repeating the same text. The top banner is reserved for backend/
      // network failures below.
      result.focus.current?.focus();
      return;
    }

    // Build full message by appending dynamic fields
    const svcDef = services.find(s => s.id === service);
    let fullMessage = message.trim();
    if (svcDef && dynValues.length > 0) {
      const extras = svcDef.fields
        .map((f, i) => dynValues[i]?.trim() ? `${f.label}: ${dynValues[i].trim()}` : null)
        .filter(Boolean);
      if (extras.length > 0) fullMessage = `${fullMessage}\n\n[${extras.join(" · ")}]`;
    }

    // ticketOrPlate: use dedicated field or the dynamic mapTo field
    const finalTicket = ticketVal.trim() || undefined;

    setSubmitting(true); setError(null);
    try {
      if (tokenMode) {
        const res = await api.submitFeedbackByToken({
          token:         props.token!,
          serviceType:   service ?? "other",
          feedbackType:  fbType ?? undefined,
          message:       fullMessage,
          rating:        rating || undefined,
          contactName:   contactName.trim()  || undefined,
          contactEmail:  contactEmail.trim() || undefined,
          contactPhone:  contactPhone.trim() || undefined,
          responseRequested: responseRequestAvailable && showContact,
          preferredContactMethod: responseRequestAvailable && showContact ? prefContact : undefined,
          ticketOrPlate: finalTicket,
        } as any);
        setReferenceNum((res as any)?.referenceNumber ?? null);
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      const code: string = err?.code ?? "";
      if (code.includes("failed-precondition")) {
        setDisabled(true);
      } else if (tokenMode) {
        setError("We couldn't submit your feedback. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Branding
  const brandName = tokenSettings.brandName ?? null;
  const logoUrl   = tokenSettings.logoUrl   ?? null;
  const formTitle = tokenSettings.formTitle ?? "Share your experience";
  const formSub   = tokenSettings.formSubtitle ?? "Your feedback helps us improve service for every resident and guest.";
  const showPoweredBy = tokenSettings.showPoweredByAtrium;

  const svcDef = services.find(s => s.id === service);

  // Dynamic step numbering: rating/type/details are always present; the
  // service step only renders when configured on. Numbers derive from the
  // visible step's position so disabling it leaves no numbering gap.
  const stepNums = (() => {
    let n = 1;
    const ratingNum = n++;
    const typeNum = n++;
    const serviceNum = showServiceStep ? n++ : null;
    const detailsNum = n++;
    return { ratingNum, typeNum, serviceNum, detailsNum };
  })();

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (tokenResolving) {
    return (
      <div style={ROOT}>
        <div style={{ ...WRAP, textAlign: "center", padding: "80px 20px 0" }} role="status" aria-live="polite">
          <div style={{ fontSize: 14, color: C.stone500 }}>Loading…</div>
        </div>
      </div>
    );
  }

  // ── Disabled ─────────────────────────────────────────────────────────────────
  if (disabled) {
    return (
      <div style={ROOT}>
        <div style={{ ...WRAP, textAlign: "center", padding: "80px 20px 0" }} role="status">
          <div style={{ fontSize: 40, marginBottom: 16 }} aria-hidden="true">🔒</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.ink, marginBottom: 10 }}>Feedback unavailable</div>
          <p style={{ fontSize: 14, color: C.stone500, lineHeight: 1.6, margin: 0 }}>
            {tokenMode ? "This feedback link is not currently available." : "Feedback is not available for this location."}
          </p>
        </div>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────────────
  if (done) {
    return (
      <div style={ROOT}>
        <div style={{ ...WRAP, textAlign: "center", padding: "30px 20px 0" }}>
          {/* Seal */}
          <div style={{
            width: 76, height: 76, borderRadius: 999, margin: "14px auto 22px",
            background: C.forest50,
            border: `1px solid color-mix(in srgb, ${C.forest500} 30%, transparent)`,
            display: "flex", alignItems: "center", justifyContent: "center", color: C.forest500,
          }}>
            <Check size={38} strokeWidth={2.2} />
          </div>
          <h2 ref={successHeadingRef} tabIndex={-1} style={{ fontFamily: FONTS.serif, fontWeight: 400, fontSize: 36, margin: 0, color: C.ink, outline: "none" }}>Thank you</h2>
          <p style={{ fontSize: 15, color: C.stone500, margin: "10px auto 0", maxWidth: "36ch", lineHeight: 1.55 }}>
            Your feedback has been received and shared with {tokenSettings.publicReviewerLabel}.
          </p>

          {referenceNum && (
            <div style={{
              margin: "26px 0", padding: 20,
              border: `1px solid ${C.stone200}`, borderRadius: 14, background: C.card,
            }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.stone400 }}>
                Reference number
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 26, fontWeight: 500, letterSpacing: "0.04em", color: C.ink, marginTop: 8 }}>
                {referenceNum}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(referenceNum).catch(() => {});
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                style={{
                  marginTop: 12, height: 34, padding: "0 14px",
                  border: `1px solid ${C.stone200}`, borderRadius: 8, background: C.card,
                  fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 600, color: C.stone600,
                  cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied!" : "Copy reference"}
              </button>
            </div>
          )}

          <div style={{ textAlign: "left", display: "flex", flexDirection: "column", gap: 14, margin: "24px 0" }}>
            {[
              { icon: <Inbox size={17} strokeWidth={2} />, title: "We review it", sub: `Your feedback was received by ${tokenSettings.publicReviewerLabel}. They can follow up if you requested a response.` },
              showContact ? { icon: <PhoneCall size={17} strokeWidth={2} />, title: "We may reach out", sub: "We'll contact you if additional information is needed." } : null,
              { icon: <CheckCircle2 size={17} strokeWidth={2} />, title: "It gets resolved", sub: "Your reference number tracks this feedback through to resolution." },
            ].filter(Boolean).map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 13 }}>
                <span style={{
                  width: 34, height: 34, borderRadius: 9, background: C.cream, color: C.brass700,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>{item!.icon}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{item!.title}</div>
                  <div style={{ fontSize: 13, color: C.stone500, marginTop: 2, lineHeight: 1.45 }}>{item!.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 24 }}>
            <button onClick={() => window.scrollTo({ top: 0 })} style={{
              height: 52, borderRadius: 13, background: C.cobalt500, color: "#fff",
              border: 0, fontFamily: FONTS.sans, fontSize: 15.5, fontWeight: 600, cursor: "pointer",
            }}>
              Done
            </button>
            <button onClick={() => {
              setDone(false); setRating(0); setFbType(null); setService(null);
              setMessage(""); setDynValues([]); setTicketVal("");
              setContactName(""); setContactPhone(""); setContactEmail("");
              setShowContact(false); setReferenceNum(null); setError(null);
              window.scrollTo({ top: 0 });
            }} style={{
              height: 48, borderRadius: 13, background: "transparent",
              color: C.stone600, border: `1px solid ${C.stone200}`,
              fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}>
              Submit another feedback
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────────
  return (
    <div style={ROOT}>
      <div style={WRAP} id="form-view">
        {/* Compact brand + title block — a single mark, not a duplicated
            top bar (see historical note at the bottom of this file), sized
            so the mark + wordmark row reads like a header (~36px) while
            "Share your experience" remains real page content below it,
            not decoration. */}
        <div style={{ textAlign: "center", padding: "22px 16px 18px" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 9, marginBottom: 14 }}>
            <span style={{
              width: 34, height: 34, borderRadius: 10, flexShrink: 0,
              background: (logoUrl && !logoFailed) ? "transparent" : `linear-gradient(160deg, ${C.brass300}, ${C.brass500})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: (logoUrl && !logoFailed) ? "none" : "0 6px 14px -6px rgba(176,137,56,0.55)",
            }}>
              {(logoUrl && !logoFailed)
                ? <img
                    src={logoUrl}
                    alt={brandName ?? "Logo"}
                    onError={() => setLogoFailed(true)}
                    style={{ width: 34, height: 34, objectFit: "contain", borderRadius: 10 }}
                  />
                : <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8}><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5a3 3 0 0 1 6 0v5" /></svg>
              }
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: C.brass700 }}>
              {brandName ?? "Atrium CMD"}
            </span>
          </div>
          <h1 style={{ fontFamily: FONTS.serif, fontWeight: 400, fontSize: 26, lineHeight: 1.15, letterSpacing: "-0.01em", margin: 0, color: C.ink }}>
            {formTitle}
          </h1>
          <p style={{ fontSize: 13.5, color: C.stone500, margin: "8px auto 0", maxWidth: "34ch", lineHeight: 1.45 }}>
            {formSub}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "6px 14px", marginTop: 14 }}>
            {[
              { icon: <UserRoundCheck size={13} strokeWidth={2} color={C.brass500} />, label: "Anonymous accepted" },
              ...(showResponseCommitment ? [{ icon: <Clock size={13} strokeWidth={2} color={C.brass500} />, label: `Response within ${tokenSettings.publicResponseTargetHours}h if requested` }] : []),
              { icon: <Lock size={13} strokeWidth={2} color={C.brass500} />,           label: "Secure submission" },
            ].map(({ icon, label }) => (
              <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 500, color: C.stone500 }}>
                {icon}{label}
              </span>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Step 1 — Rating */}
          <div style={SEC} ref={ratingSectionRef} tabIndex={-1}>
            <StepHeader num={stepNums.ratingNum} title="How was your experience?" optional={!tokenSettings.requireRating} />
            <StarRating rating={rating} onChange={setRating} />
            {fieldErrors.rating && <FieldError id="gf-rating-error">{fieldErrors.rating}</FieldError>}
          </div>

          {/* Step 2 — Feedback type */}
          <div style={SEC}>
            <StepHeader num={stepNums.typeNum} title="What kind of feedback is this?" />
            <div role="group" aria-label="What kind of feedback is this?" className="gf-type-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {TYPES.map(def => (
                <TypeCard
                  key={def.id} def={def}
                  selected={fbType === def.id}
                  onClick={() => setFbType(fbType === def.id ? null : def.id)}
                />
              ))}
            </div>
          </div>

          {/* Step 3 — Service. Omitted entirely when the question is disabled
              OR when the saved config has zero active options (showServiceStep
              covers both — see its definition above) — never rendered as an
              empty/explanatory placeholder. */}
          {showServiceStep && stepNums.serviceNum !== null && (
            <div style={SEC} ref={serviceSectionRef} tabIndex={-1}>
              <StepHeader num={stepNums.serviceNum} title="Which team or service?" optional={!tokenSettings.serviceQuestionRequired} />
              <div role="group" aria-label="Which team or service?" style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
                {services.map(svc => (
                  <ServiceChip
                    key={svc.id} svc={svc}
                    selected={service === svc.id}
                    onClick={() => setService(svc.id)}
                  />
                ))}
              </div>
              {fieldErrors.service && <FieldError id="gf-service-error">{fieldErrors.service}</FieldError>}
            </div>
          )}

          {/* Step 4 — Details */}
          <div style={SEC}>
            <StepHeader num={stepNums.detailsNum} title="Tell us more" />
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              <textarea
                id="gf-message"
                ref={messageRef}
                aria-label="Tell us more about your experience"
                aria-required="true"
                aria-invalid={!!fieldErrors.message}
                aria-describedby={fieldErrors.message ? "gf-message-error" : undefined}
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={svcDef?.placeholder ?? "Share what happened — the more detail, the better we can help."}
                maxLength={1800}
                spellCheck
                autoComplete="off"
                style={{
                  ...INP, height: "auto", minHeight: 120,
                  padding: "13px 14px", resize: "vertical", lineHeight: 1.5,
                  fontFamily: FONTS.sans,
                  borderColor: fieldErrors.message ? C.rust500 : C.stone200,
                }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                {fieldErrors.message ? <FieldError id="gf-message-error">{fieldErrors.message}</FieldError> : <span />}
                <span style={{ fontSize: 11.5, color: C.stone400, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                  {message.length}/1800
                </span>
              </div>
            </div>

            {/* Dynamic fields */}
            {svcDef && svcDef.fields.length > 0 && (
              <div className={`gf-field-grid${svcDef.fields.length === 1 ? " gf-field-grid-single" : ""}`} style={{
                display: "grid",
                gridTemplateColumns: svcDef.fields.length === 1 ? "1fr" : "1fr 1fr",
                gap: 12, marginTop: 4,
              }}>
                {svcDef.fields.map((f, i) => (
                  <div key={i} style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                    <label htmlFor={`dyn-${i}`} style={{ fontSize: 13, fontWeight: 600, color: C.stone600 }}>
                      {f.label}{" "}
                      {f.optional && <span style={{ color: C.stone400, fontWeight: 500, fontSize: 11.5 }}>optional</span>}
                    </label>
                    <input
                      id={`dyn-${i}`}
                      type="text"
                      placeholder={f.placeholder}
                      value={f.mapTo === "ticketOrPlate" ? ticketVal : (dynValues[i] ?? "")}
                      onChange={e => {
                        if (f.mapTo === "ticketOrPlate") {
                          setTicketVal(e.target.value);
                        } else {
                          setDynValues(v => { const n = [...v]; n[i] = e.target.value; return n; });
                        }
                      }}
                      style={INP}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {responseRequestAvailable && <>{/* Contact card — forced open and non-collapsible when the location
              requires contact info for anonymous-disallowed feedback. */}
          <button
            type="button"
            onClick={() => { if (!requireContact) setShowContact(c => !c); }}
            aria-expanded={showContact}
            disabled={requireContact}
            style={{
              display: "flex", alignItems: "center", gap: 12, width: "100%",
              padding: "16px 18px", border: `1px solid ${fieldErrors.contact ? C.rust500 : C.stone200}`, borderRadius: 14,
              background: C.card, cursor: requireContact ? "default" : "pointer", fontFamily: FONTS.sans, textAlign: "left",
              transition: "border-color 140ms",
            }}
          >
            <span style={{
              width: 40, height: 40, borderRadius: 11, background: C.cobalt50,
              color: C.cobalt500, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Bell size={19} strokeWidth={2} />
            </span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>
                {requireContact ? "Contact information" : "Need a response?"}
              </div>
              <div style={{ fontSize: 12.5, color: C.stone500, marginTop: 2 }}>
                {requireContact
                  ? "Required — used only to follow up on this feedback."
                  : "Optional — receive updates about this feedback"}
              </div>
            </div>
            {!requireContact && (
              <span style={{
                marginLeft: "auto", color: C.stone400,
                transform: showContact ? "rotate(45deg)" : "none",
                transition: "transform 180ms",
                display: "flex",
              }}>
                <Plus size={18} />
              </span>
            )}
          </button>
          {fieldErrors.contact && <FieldError id="gf-contact-error">{fieldErrors.contact}</FieldError>}

          {showContact && (
            <div style={{ ...SEC, paddingTop: 18 }}>
              <div className="gf-contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  <label htmlFor="gf-contact-name" style={{ fontSize: 13, fontWeight: 600, color: C.stone600 }}>
                    Name {!requireContact && <span style={{ color: C.stone400, fontWeight: 500, fontSize: 11.5 }}>optional</span>}
                  </label>
                  <input
                    id="gf-contact-name"
                    ref={contactNameRef}
                    type="text"
                    autoComplete="name"
                    aria-invalid={!!fieldErrors.contact}
                    aria-describedby={fieldErrors.contact ? "gf-contact-error" : undefined}
                    placeholder="Your name"
                    value={contactName}
                    onChange={e => setContactName(e.target.value)}
                    style={{ ...INP, borderColor: fieldErrors.contact ? C.rust500 : C.stone200 }}
                  />
                </div>
                {(allowedContactMethods.includes("sms") || allowedContactMethods.includes("phone")) && <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  <label htmlFor="gf-contact-phone" style={{ fontSize: 13, fontWeight: 600, color: C.stone600 }}>
                    Phone <span style={{ color: C.stone400, fontWeight: 500, fontSize: 11.5 }}>optional</span>
                  </label>
                  <input id="gf-contact-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="(212) 555-0100" value={contactPhone} onChange={e => setContactPhone(e.target.value)} style={INP} />
                </div>}
                {allowedContactMethods.includes("email") && <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 7 }}>
                  <label htmlFor="gf-contact-email" style={{ fontSize: 13, fontWeight: 600, color: C.stone600 }}>
                    Email {!requireContact && <span style={{ color: C.stone400, fontWeight: 500, fontSize: 11.5 }}>optional</span>}
                  </label>
                  <input
                    id="gf-contact-email"
                    type="email" inputMode="email" autoComplete="email"
                    aria-invalid={!!fieldErrors.contact}
                    aria-describedby={fieldErrors.contact ? "gf-contact-error" : undefined}
                    placeholder="you@email.com"
                    value={contactEmail}
                    onChange={e => setContactEmail(e.target.value)}
                    style={{ ...INP, borderColor: fieldErrors.contact ? C.rust500 : C.stone200 }}
                  />
                </div>}
              </div>
              <fieldset style={{ marginTop: 12, border: "none", padding: 0 }}>
                <legend style={{ fontSize: 13, fontWeight: 600, color: C.stone600, marginBottom: 8, padding: 0 }}>
                  Preferred contact method
                </legend>
                <div style={{ display: "flex", gap: 8 }}>
                  {([
                    { id: "sms" as PrefContact,   icon: <MessageSquare size={14} />, label: "SMS"   },
                    { id: "phone" as PrefContact, icon: <Phone size={14} />,         label: "Phone" },
                    { id: "email" as PrefContact, icon: <Mail size={14} />,          label: "Email" },
                  ]).filter(p => allowedContactMethods.includes(p.id)).map(p => (
                    <button
                      key={p.id} type="button" aria-pressed={prefContact === p.id}
                      onClick={() => setPrefContact(p.id)}
                      style={{
                        flex: 1, height: 44, border: `1px solid ${prefContact === p.id ? C.cobalt500 : C.stone200}`,
                        borderRadius: 9,
                        background: prefContact === p.id ? C.cobalt50 : C.card,
                        fontFamily: FONTS.sans, fontSize: 13, fontWeight: 600,
                        color: prefContact === p.id ? C.cobalt500 : C.stone600,
                        cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                        transition: "all 140ms",
                      }}
                    >
                      {p.icon}{p.label}
                    </button>
                  ))}
                </div>
              </fieldset>
            </div>
          )}
          </>}

          {/* Trust box */}
          <div style={{
            display: "flex", gap: 11, padding: "14px 16px", borderRadius: 12,
            background: C.cream, marginTop: 4,
          }}>
            <ShieldCheck size={17} color={C.brass600} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ margin: 0, fontSize: 12.5, color: C.stone600, lineHeight: 1.5 }}>
              Your feedback is reviewed by {tokenSettings.publicReviewerLabel}. A <strong style={{ color: C.ink, fontWeight: 600 }}>reference number</strong> is issued for every submission so you can follow up.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div role="alert" style={{
              padding: "12px 16px", borderRadius: 12, background: `${C.rust50}`,
              color: C.rust500, fontSize: 14, border: `1px solid rgba(176,67,47,0.25)`, lineHeight: 1.5,
            }}>
              {error}
            </div>
          )}

          {/* Submit — enabled whenever not mid-submission; validation runs on
              attempt and explains what's missing via inline field errors +
              focus, rather than leaving the button silently disabled. */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              ...SUBMIT_BASE,
              background: submitting ? C.stone300 : C.cobalt500,
              boxShadow: submitting ? "none" : SUBMIT_BASE.boxShadow,
              cursor: submitting ? "not-allowed" : "pointer",
            }}
          >
            {submitting
              ? <span style={{ width: 19, height: 19, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: 999, animation: "spin 0.7s linear infinite", display: "inline-block" }} />
              : <><Send size={19} /><span>Submit feedback</span></>
            }
          </button>

          <div style={{ textAlign: "center", fontSize: 12, color: C.stone400, marginTop: 18, lineHeight: 1.5 }}>
            {showPoweredBy ? <>Powered by <strong style={{ color: C.stone500 }}>Atrium</strong></> : null}
          </div>
        </form>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        #form-view button:focus-visible,
        #form-view input:focus-visible,
        #form-view textarea:focus-visible,
        #form-view select:focus-visible {
          outline: 2px solid ${C.cobalt500};
          outline-offset: 2px;
        }
        @media (prefers-reduced-motion: reduce) {
          #form-view *, #form-view *::before, #form-view *::after {
            animation-duration: 0.001ms !important;
            transition-duration: 0.001ms !important;
          }
        }
      `}</style>
    </div>
  );
}

// TopBar removed (Building Front Desk / public feedback branding cleanup):
// this narrow black-icon-and-wordmark bar duplicated the page's own
// intentional branding (the "hospitality header" logo block + formTitle,
// e.g. "Share your experience", and the "Powered by Atrium" footer). The
// public feedback route now shows only the intentional branding.
